import { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';

import { httpStatusCodes } from '@common/Constants';
import Logger from '@common/Logger.js';
import { BOOKING_PROCESS } from '@common/network';
import { checkIsEventFree } from '@common/Util';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { participantsOptions, VAT_BOOKING_PROCESS } from '@pages/BookingProcess/Constants';
import { ConditionTypes, DiscountTypes } from '@pages/EventDetails/Constants';
import { addStandardPrice } from '@pages/EventDetails/Util';
import { getByPathAndParams } from '@services/BaseApi';

import contactInformationInitValues, {
  prepopulatedContactInformation
} from '../ContactInformation/initialValues';

export const DEFAULT_LOGIN_USER = {
  generalInformation: {
    title: '2',
    gender: '2',
    firstName: 'Tobias',
    lastName: 'Rothley',
    language: '1',
    company: 'Orbis/Contrimo'
  },
  contactInformation: {
    street: 'Konrad-Zuse-Ring',
    number: '23',
    city: 'MannHeim',
    postCode: '68163',
    region: '14',
    country: '1',
    email: 'tobias.rothley@contrimo.com',
    mobile: '+49(160)90410226',
    phone: ''
  }
};

const initTotalObj = {
  subtotal: 0,
  total: 0
};

const getDiscountCalculation = ({
  discountType = DiscountTypes.PERCENTAGE,
  discountValue = 0,
  discountName = '',
  priceCurrency = '',
  basePrice = null
}) => {
  let displayValue = '';
  let count = 0;
  let discountAmount = 0;

  switch (discountType) {
    case DiscountTypes.PERCENTAGE: {
      displayValue = `${discountName} - ${discountValue}%`;
      count = parseFloat((discountValue / 100).toFixed(4));
      discountAmount = count * basePrice;
      break;
    }
    case DiscountTypes.FIXED_PRICE: {
      displayValue = `${discountName} - ${discountValue} ${priceCurrency}`;
      count = parseFloat(discountValue);
      discountAmount = count;
      break;
    }
    default:
      displayValue = '';
      count = 0;
      discountAmount = 0;
  }
  return { displayValue, count, discountAmount };
};

const calculateDiscountOptions = (discounts, priceCurrency = '') => {
  const temp = [
    {
      id: 0,
      value: Localize.get('App.none'),
      count: 0,
      isDefault: false,
      type: null,
      originalValue: null
    }
  ];
  discounts?.forEach(({ id, type, value, name, unitType }) => {
    if ([ConditionTypes.MANUAL_DISCOUNT, ConditionTypes.AUTOMATIC_DISCOUNT].includes(type?.id)) {
      const unitTypeKey = unitType?.key;

      const { displayValue, count } = getDiscountCalculation({
        discountType: unitTypeKey,
        discountValue: value,
        discountName: name,
        priceCurrency: priceCurrency
      });

      temp.push({
        id: id,
        value: displayValue,
        count: count,
        isDefault: type?.id === ConditionTypes.AUTOMATIC_DISCOUNT,
        type: unitTypeKey,
        originalValue: value
      });
    }
  });
  return temp;
};

export const getInitContactInformation = (isEventFree) => {
  return isEventFree
    ? {
        ...contactInformationInitValues,
        generalInformation: {
          ...contactInformationInitValues.generalInformation,
          language: ''
        }
      }
    : contactInformationInitValues;
};

const useBookingProcess = (eventData, configuration) => {
  const [contextData, setContextData] = useGlobalContext();
  const isEventFree = checkIsEventFree(eventData);

  const steps = configuration?.BookingProcess?.bookingSteps;

  const [activeStep, setActiveStep] = useState(0);
  const [availableSteps, setAvailableSteps] = useState([]);

  const [isFormValid, setIsFormValid] = useState(true);
  const [isCheckoutFormValid, setIsCheckoutFormValid] = useState(false);
  const isLoggedIn = contextData?.loggedIn;

  const {
    id: eventId,
    uuid: eventUuid,
    venues,
    price: eventPrice,
    name: eventName,
    hasWaitingList: eventHasWaitingList,
    availableSeats: eventAvailableSeats
  } = eventData;

  const [isLoading, setIsLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionsIds, setSelectedSessionsIds] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [totalObj, setTotalObj] = useState({
    ...initTotalObj,
    eventPrice: eventPrice,
    eventName: eventName,
    currency: eventData?.standardPrice?.currency?.key || ''
  });
  const [participantPriceAndDiscounts, setParticipantPriceAndDiscounts] = useState({
    ...initTotalObj,
    eventPrice: eventPrice,
    eventName: eventName,
    currency: eventData?.standardPrice?.currency?.key || ''
  });
  const [availableSeats, setAvailableSeats] = useState({
    id: eventUuid,
    name: eventName,
    count: eventAvailableSeats,
    hasWaitingList: eventHasWaitingList,
    sessions: []
  });
  const [availableSeatsErrors, setAvailableSeatsErrors] = useState(null);
  const [contactsInfo, setContactsInfo] = useState(
    eventData?.eventParticipant
      ? prepopulatedContactInformation(eventData?.eventParticipant, isEventFree)
      : isLoggedIn
        ? { ...getInitContactInformation(isEventFree), ...DEFAULT_LOGIN_USER }
        : getInitContactInformation(isEventFree)
  );
  const [participants, setParticipants] = useState([]);
  const [bookingPersonRadio, setBookingPersonRadio] = useState(participantsOptions.participant);
  const [discountOptions, setDiscountOptions] = useState(null);
  const [checkedConsents, setCheckedConsents] = useState({});

  const participantWithoutBookingPerson = participants.filter(
    (participant) => !participant.isBookingPerson
  );

  const handleSession = (event, data) => {
    const idsCopy = [...selectedSessionsIds];
    const sessionId = parseInt(event.target.id);
    if (data) {
      idsCopy.push(sessionId);
      setSelectedSessionsIds(idsCopy);
    } else {
      let filterData = idsCopy.filter((id) => id !== sessionId);
      setSelectedSessionsIds(filterData);
    }
  };

  const removeSession = (id) => {
    setSelectedSessionsIds((prev) => prev.filter((sessionId) => sessionId !== id));
    if (participants?.[0]?.discounts) {
      setParticipants((prev) =>
        prev.map((participant) => {
          if (participant?.discounts) {
            return {
              ...participant,
              discounts: {
                ...participant?.discounts,
                optionalSessionsDiscounts:
                  participant?.discounts?.optionalSessionsDiscounts?.filter(
                    (discount) => discount?.sessionId !== id
                  )
              }
            };
          } else {
            return participant;
          }
        })
      );
    }
  };

  const fetchEventSessions = async () => {
    try {
      const response = await getByPathAndParams({
        path: BOOKING_PROCESS.GET_SESSIONS_FOR_EVENTS,
        pathVariables: { eventId: eventUuid },
        temApi: true
      });

      if (response?.status === httpStatusCodes.ok) {
        // sort mandatory items first in the array
        const sortData =
          response?.data?.content?.sort((a, b) => {
            if (a?.optionalSession !== b?.optionalSession) {
              return a?.optionalSession - b?.optionalSession;
            }
            if (a?.startDateTime && b?.startDateTime) {
              return a.startDateTime.localeCompare(b.startDateTime);
            }
            return 0;
          }) || [];
        const tempSessions = sortData.map((session) => ({
          ...addStandardPrice(session),
          venues: venues
        }));
        setSessions(tempSessions);
        const sessionCurrency = tempSessions
          .flatMap((s) => s?.conditions || [])
          .find((c) => c?.currency)?.currency?.key;

        if (sessionCurrency) {
          setTotalObj((prev) => (prev.currency ? prev : { ...prev, currency: sessionCurrency }));
          setParticipantPriceAndDiscounts((prev) =>
            prev.currency ? prev : { ...prev, currency: sessionCurrency }
          );
        }
        setDiscountOptions({
          event: calculateDiscountOptions(
            eventData?.conditions,
            eventData?.standardPrice?.currency?.value
          ),
          optionalSessions: tempSessions
            ?.filter((session) => session?.optionalSession)
            .map((session) => ({
              sessionId: session?.id,
              discounts: calculateDiscountOptions(
                session?.conditions,
                eventData?.standardPrice?.currency?.value
              )
            }))
        });
      }
    } catch (err) {
      Logger.error(err);
    }
  };

  const checkValue = (id) => {
    return selectedSessionsIds.includes(id);
  };

  const setWaitingListApproved = (sessionId) => {
    if (!sessionId) {
      setAvailableSeats((prev) => ({ ...prev, waitingListApproved: true }));
    }
    setAvailableSeats((prev) => ({
      ...prev,
      sessions: prev.sessions.map((session) => {
        if (session.id === sessionId) {
          return { ...session, waitingListApproved: true };
        }
        return session;
      })
    }));
  };

  const fetchAvailableSeats = async () => {
    try {
      const { data } = await getByPathAndParams({
        path: BOOKING_PROCESS.GET_AVAILABLE_SEATS,
        pathVariables: { eventId },
        temApi: true
      });

      setAvailableSeats((prev) => ({
        ...prev,
        count: data?.count,
        waitingListApproved: !!prev?.waitingListApproved,
        sessions: data?.sessions
          ? data?.sessions?.map((session) => ({
              ...session,
              hasWaitingList:
                sessions?.find((item) => item?.id === session?.id)?.hasWaitingList || false,
              waitingListApproved: !!prev?.sessions?.find((item) => item?.id === session?.id)
                ?.waitingListApproved
            }))
          : []
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFormResources = async () => {
    try {
      if (!contextData?.bookingFormData) {
        const { data } = await getByPathAndParams({
          path: BOOKING_PROCESS.GET_FORM_RESOURCES,
          temApi: true
        });

        const title = data?.title?.filter((title) => title?.active);
        const academicTitle = data?.academictitle?.filter((title) => title?.active);

        setContextData((prev) => ({
          ...prev,
          bookingFormData: {
            ...data,
            title: [
              { id: 0, value: Localize.get('App.none'), key: 'NONE', active: true },
              ...(title || [])
            ],
            academicTitle: [...(academicTitle || [])],
            nativelanguage:
              import.meta.env.VITE_CLIENT_BRAND === 'fraunhofer'
                ? data?.nativelanguage.filter((lang) => lang.key === 'DE' || lang.key === 'EN')
                : data?.nativelanguage,
            industrySectorOptions: [
              { id: 0, value: Localize.get('App.none'), key: 'NONE', active: true },
              ...(data?.industrysector || [])
            ]
          }
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    const fetchAll = async () => {
      await fetchEventSessions();
      await fetchFormResources();
      setIsLoading(false);
    };
    fetchAll();
  }, []);

  useEffect(() => {
    setIsFormValid(true);
    fetchAvailableSeats();
  }, [activeStep]);

  useEffect(() => {
    const skipSteps = [];
    if (!sessions.length) skipSteps.push(0); // skip session step
    if (
      !eventData?.enableHotelRequest &&
      !eventData?.questionnaireEnabled &&
      !eventData?.customFields?.length
    ) {
      skipSteps.push(3); // skip additional information step
    }

    if (
      !eventData?.conditions?.some((item) =>
        [ConditionTypes.MANUAL_DISCOUNT, ConditionTypes.AUTOMATIC_DISCOUNT].includes(item?.type?.id)
      ) &&
      !selectedSessions.some(
        (session) =>
          !!session?.conditions?.some((item) =>
            [ConditionTypes.MANUAL_DISCOUNT, ConditionTypes.AUTOMATIC_DISCOUNT].includes(
              item?.type?.id
            )
          )
      )
    ) {
      skipSteps.push(4); // skip discounts step
    }

    if (steps?.length) {
      setAvailableSteps(
        steps.filter((step) => !skipSteps.includes(step?.id)).map((step) => step?.id)
      );
    }
  }, [steps, sessions.length, selectedSessions.length]);

  useEffect(() => {
    if (participantWithoutBookingPerson.length === 0 && activeStep) {
      setIsFormValid(false);
      return;
    }
    setIsFormValid(true);
  }, [participants]);

  useEffect(() => {
    const mandatoryElements = sessions?.filter((session) => !session?.optionalSession);
    const mandatoryIds = mandatoryElements.map((el) => el.id);
    setSelectedSessionsIds(mandatoryIds);
    if (sessions.length) fetchAvailableSeats();
  }, [sessions]);

  useEffect(() => {
    const selectedElements = sessions.filter((item) => selectedSessionsIds.includes(item.id));
    const optionalSessionsPriceSum = selectedElements
      .filter((session) => session.optionalSession)
      .map((el) => el?.price ?? 0)
      .reduce((accumulator, currentValue) => {
        return accumulator + currentValue;
      }, 0);

    const sum = optionalSessionsPriceSum + totalObj?.eventPrice;

    const vat = VAT_BOOKING_PROCESS * sum;
    setSelectedSessions(selectedElements);
    setTotalObj({
      ...totalObj,
      subtotal: sum,
      vat: vat,
      total: sum + vat
    });
  }, [selectedSessionsIds]);

  useEffect(() => {
    const participantShowDiscounts = [];
    const participantWithoutBookingPerson = participants.filter(
      (participant) => !participant.isBookingPerson
    );

    const sum = participantWithoutBookingPerson.reduce((accumulator, participant) => {
      const sessionShowDiscounts = [];

      const participantSum = selectedSessions.reduce(
        (participantOptionalSessionAccumulator, session) => {
          let participantDiscount = 0;

          if (participant?.discounts?.optionalSessionsDiscounts && session.optionalSession) {
            let discountObject = {};

            const foundDiscount =
              participant.discounts.optionalSessionsDiscounts.find(
                (item) => item?.sessionId === session?.id
              ) || 0;

            discountObject = discountOptions?.optionalSessions
              ?.find((i) => i?.sessionId === session?.id)
              ?.discounts?.find((item) => item?.id === foundDiscount?.value);
            const { discountAmount } = getDiscountCalculation({
              discountType: discountObject?.type,
              discountValue: discountObject?.originalValue,
              basePrice: session?.price
            });
            participantDiscount = discountAmount;

            if (participantDiscount) {
              sessionShowDiscounts.push({
                id: session?.id,
                discountName: discountObject?.value,
                discountValue: participantDiscount,
                count: discountObject?.count
              });
            }
          }
          return participantOptionalSessionAccumulator + session.price - participantDiscount;
        },
        0
      );

      let eventParticipantSum = totalObj.eventPrice;
      let discountObject = {};
      let participantDiscount = 0;
      if (participant?.discounts?.eventDiscount) {
        discountObject = discountOptions?.event?.find(
          (item) => item?.id === participant.discounts.eventDiscount
        );
        const { discountAmount } = getDiscountCalculation({
          discountType: discountObject?.type,
          discountValue: discountObject?.originalValue,
          basePrice: totalObj?.eventPrice
        });
        participantDiscount = discountAmount;
      }

      participantShowDiscounts.push({
        mandatorySessions:
          participant?.discounts?.eventDiscount && participantDiscount
            ? {
                discountName: discountObject?.value,
                discountValue: participantDiscount,
                count: discountObject?.count
              }
            : null,
        optionalSessions: [...sessionShowDiscounts]
      });

      return accumulator + participantSum + eventParticipantSum - participantDiscount;
    }, 0);

    const vat = VAT_BOOKING_PROCESS * sum;

    setParticipantPriceAndDiscounts({
      ...participantPriceAndDiscounts,
      subtotal: sum,
      vat: vat,
      total: sum + vat,
      showDiscounts: [...participantShowDiscounts]
    });
  }, [participants, totalObj]);

  const calculateAvailableSeatsErrors = () => {
    const selectedOptionalSessions = selectedSessions.filter((session) => session.optionalSession);

    if (
      availableSeats.count === 0 &&
      (!availableSeats.hasWaitingList || // has not waiting list
        (availableSeats.hasWaitingList && !availableSeats?.waitingListApproved))
    ) {
      setAvailableSeatsErrors({ noSpace: true, confirmAndContinue: availableSeats.hasWaitingList });
      return;
    }

    const participantsNumber = participantWithoutBookingPerson.length;
    const temp = {};

    if (
      availableSeats.count < participantsNumber &&
      (!availableSeats.hasWaitingList || // has not waiting list
        (availableSeats.hasWaitingList && !availableSeats?.waitingListApproved)) // has waiting list but user not approved using it
    ) {
      temp.isSession = false;
      temp.seats = availableSeats.count;
      temp.name = availableSeats.name;
      temp.confirmAndContinue = availableSeats.hasWaitingList;
      setAvailableSeatsErrors(temp);
      return;
    }

    if (availableSeats?.sessions?.length) {
      temp.isSession = true;
      temp.seats = [];
      temp.name = [];
      temp.confirmAndContinue = [];
      temp.ids = [];
      temp.sessionGreaterThenEvent = false;

      if (availableSeats?.count < participantsNumber) {
        selectedOptionalSessions.forEach((session) => {
          const findSession = availableSeats?.sessions?.find((item) => item.id === session.id);

          if (findSession) {
            const sessionSeatsCount = findSession.count;
            const hasWaitingList = findSession.hasWaitingList;

            if (sessionSeatsCount > availableSeats?.count && !hasWaitingList) {
              temp.seats.push(sessionSeatsCount);
              temp.name.push(session.name);
              temp.confirmAndContinue.push(hasWaitingList);
              temp.ids.push(session.id);
              temp.sessionGreaterThenEvent = true;
            }
          }
        });

        if (temp?.sessionGreaterThenEvent) {
          setAvailableSeatsErrors(temp);
          return;
        }
      }

      selectedOptionalSessions.forEach((session) => {
        const findSession = availableSeats.sessions.find((item) => item.id === session.id);

        if (findSession) {
          const sessionSeatsCount = findSession.count;
          const hasWaitingList = findSession.hasWaitingList;
          const waitingListApproved = findSession.waitingListApproved;

          if (
            sessionSeatsCount < participantsNumber &&
            (!hasWaitingList || // has not waiting list
              (hasWaitingList && !waitingListApproved)) // has waiting list but user not approved using it
          ) {
            temp.seats.push(sessionSeatsCount);
            temp.name.push(session.name);
            temp.confirmAndContinue.push(hasWaitingList);
            temp.ids.push(session.id);
          }
        }
      });
    }

    setAvailableSeatsErrors(temp?.seats?.length ? temp : null);
  };

  useEffect(() => {
    calculateAvailableSeatsErrors();
  }, [availableSeats, activeStep, participants, selectedSessions]);

  return {
    isLoading,
    sessions,
    handleSession,
    selectedSessions,
    totalObj,
    checkValue,
    contactsInfo,
    setContactsInfo,
    participants,
    setParticipants,
    bookingPersonRadio,
    setBookingPersonRadio,
    participantPriceAndDiscounts,
    availableSeats,
    fetchAvailableSeats,
    isFormValid,
    setIsFormValid,
    activeStep,
    setActiveStep,
    steps,
    availableSteps,
    contextData,
    isLoggedIn,
    isCheckoutFormValid,
    setIsCheckoutFormValid,
    availableSeatsErrors,
    setWaitingListApproved,
    removeSession,
    discountOptions,
    checkedConsents,
    setCheckedConsents
  };
};

export default useBookingProcess;
